// frontend/src/pages/MeusNegocios.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  criarNegocio,
  listenNegociosVisiveis,
  atualizarNegocio,
  getNegocioPrivado,
} from '../services/negociosService';

import NegocioFormModal from '../components/negocios/NegocioFormModal';
import NegocioCard from '../components/negocios/NegocioCard';
import NegocioDetailsModal from '../components/negocios/NegocioDetailsModal';
import MatchCard from '../components/negocios/MatchCard';
import MatchCardModal from '../components/negocios/MatchCardModal';
import NegociosFiltroBar from '../components/negocios/NegociosFiltroBar';
import TipoTabsBar from '../components/negocios/TipoTabsBar';
import { useNavigate } from 'react-router-dom';

import {
  gerarMatchesParaNegocio,
  listenMatchesDoNegocio,
} from '../services/matchService';

/* =============================
   Helpers
============================= */
function normalize(str = '') {
  return String(str)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function aceitaPermutaBool(n) {
  return !!n?.aceitaPermuta || !!n?.permuta?.aceita;
}

function matchFaixaValor(n, faixa) {
  const vMin = Number(n.valorMin ?? 0);
  const vMax = Number(n.valorMax ?? vMin);

  if (faixa === 'ate200') return vMax <= 200000;
  if (faixa === '200a400') return vMin >= 200000 && vMax <= 400000;
  if (faixa === '400a600') return vMin >= 400000 && vMax <= 600000;
  if (faixa === '600a800') return vMin >= 600000 && vMax <= 800000;
  if (faixa === '800a1m') return vMin >= 800000 && vMax <= 1000000;
  if (faixa === 'acima1m') return vMin >= 1000000;
  return true;
}

function rankStatusVal(s) {
  const x = String(s || '').toUpperCase();
  if (x === 'VERDE') return 1;
  if (x === 'AMARELO') return 2;
  if (x === 'VERMELHO') return 3;
  return 9;
}

function digitsOnly(v = '') {
  return String(v || '').replace(/[^\d]/g, '');
}

function toWhatsAppLink(rawPhone) {
  const d = digitsOnly(rawPhone);
  if (!d) return null;
  const withDDD = d.startsWith('55') ? d : `55${d}`;
  return `https://wa.me/${withDDD}`;
}

/**
 * ✅ aplica todos filtros EXCETO "tipoImovel"
 */
function aplicarFiltrosSemTipo(lista = [], filtros = {}, aba = 'rede') {
  return (lista || []).filter((n) => {
    // Corretor (apenas na rede)
    if (aba === 'rede' && filtros.corretorId && filtros.corretorId !== 'all') {
      if (n.corretorId !== filtros.corretorId) return false;
    }

    // Bairro
    const bairrosDoc = (n.bairros || n.bairrosDesejados || []).map((b) =>
      String(b).trim()
    );
    if (filtros.bairro && !bairrosDoc.includes(filtros.bairro)) return false;

    // Permuta
    if (filtros.permuta) {
      const aceita = aceitaPermutaBool(n);
      if (filtros.permuta === 'sim' && !aceita) return false;
      if (filtros.permuta === 'nao' && aceita) return false;
    }

    // Faixa valor
    if (filtros.faixaValor && !matchFaixaValor(n, filtros.faixaValor))
      return false;

    // Pesquisa
    if (filtros.pesquisa) {
      const texto = normalize(filtros.pesquisa);

      const blobRede = normalize(`
        ${n.corretorNome || ''}
        ${n.tipoImovel || ''}
        ${(bairrosDoc || []).join(', ')}
        ${n.cidade || n.cidadePrincipal || ''}
        ${n.descricao || ''}
        ${n.objetivo || ''}
        ${n.tituloNegocio || ''}
        ${n.regiao || n.zona || n.setor || ''}
      `);

      const blobMeus = normalize(`
        ${n.nomeCliente || ''}
        ${n.corretorNome || ''}
        ${n.tipoImovel || ''}
        ${(bairrosDoc || []).join(', ')}
        ${n.cidade || n.cidadePrincipal || ''}
        ${n.descricao || ''}
        ${n.objetivo || ''}
        ${n.tituloNegocio || ''}
        ${n.regiao || n.zona || n.setor || ''}
      `);

      const blob = aba === 'rede' ? blobRede : blobMeus;
      if (!blob.includes(texto)) return false;
    }

    return true;
  });
}

/* =============================
   ✅ Header de colunas
============================= */
function NegociosColumnsHeader() {
  return (
    <div className="hidden md:block w-full px-6 mt-3 mb-2">
      <div className="grid md:grid-cols-12 gap-4 items-center">
        <div className="md:col-span-4 text-[11px] font-bold tracking-widest text-gray-400 uppercase">
          Tipo & Objetivo
        </div>
        <div className="md:col-span-3 text-[11px] font-bold tracking-widest text-gray-400 uppercase">
          Ticket
        </div>
        <div className="md:col-span-3 text-[11px] font-bold tracking-widest text-gray-400 uppercase">
          Local & Flexibilidade
        </div>
        <div className="md:col-span-2 text-[11px] font-bold tracking-widest text-gray-400 uppercase">
          Autor
        </div>
      </div>
    </div>
  );
}

export default function MeusNegocios() {
  const { usuario } = useAuth() || {};
  const navigate = useNavigate();

  const [aba, setAba] = useState('rede'); // 'rede' | 'meus'

  const [negociosAll, setNegociosAll] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [salvando, setSalvando] = useState(false);

  // ✅ MODAL EXPANDIDO (Ver detalhes)
  const [negocioAberto, setNegocioAberto] = useState(null);

  const [matchAbertoId, setMatchAbertoId] = useState(null);
  const [matchAberto, setMatchAberto] = useState(null);

  const [matchesByNegocio, setMatchesByNegocio] = useState({});
  const [loadingMatchId, setLoadingMatchId] = useState(null);
  const [unsubMatchByNegocio, setUnsubMatchByNegocio] = useState({});

  const [filtros, setFiltros] = useState({
    corretorId: 'all',
    tipoImovel: '',
    faixaValor: '',
    bairro: '',
    permuta: '',
    pesquisa: '',
  });

  // ✅ ouvir negócios visíveis (seus + sócios)
  useEffect(() => {
    if (!usuario?.uid) return;
    const unsub = listenNegociosVisiveis(usuario.uid, setNegociosAll);
    return unsub;
  }, [usuario?.uid]);

  // cleanup listeners matches
  useEffect(() => {
    return () => {
      Object.values(unsubMatchByNegocio || {}).forEach((fn) => {
        if (typeof fn === 'function') fn();
      });
    };
  }, [unsubMatchByNegocio]);

  // ✅ listas por aba
  const negociosRede = useMemo(() => negociosAll || [], [negociosAll]);

  const negociosMeus = useMemo(() => {
    const uid = usuario?.uid;
    return (negociosAll || []).filter((n) => n?.corretorId === uid);
  }, [negociosAll, usuario?.uid]);

  const listaAtual = aba === 'rede' ? negociosRede : negociosMeus;

  // ✅ CONTADORES (para as tabs Rede/Meus)
  const totalRede = negociosRede?.length || 0;
  const totalMeus = negociosMeus?.length || 0;

  // ✅ corretores para o select (apenas na Rede) com count
  const corretoresSelect = useMemo(() => {
    const map = new Map();

    (negociosRede || []).forEach((n) => {
      const id = n?.corretorId;
      if (!id) return;

      const nome =
        n?.corretorNome ||
        (id === usuario?.uid
          ? usuario?.nome || usuario?.displayName || 'Eu'
          : 'Corretor');

      const prev = map.get(id);
      if (!prev) map.set(id, { id, nome, count: 1 });
      else map.set(id, { ...prev, count: (prev.count || 0) + 1 });
    });

    return Array.from(map.values()).sort((a, b) =>
      String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR')
    );
  }, [negociosRede, usuario?.uid, usuario?.nome, usuario?.displayName]);

  // ✅ listas dinâmicas (com base na lista atual)
  const { tiposUnicos, bairrosUnicos } = useMemo(() => {
    const tipos = Array.from(
      new Set((listaAtual || []).map((n) => n.tipoImovel).filter(Boolean))
    ).sort();

    const bairros = Array.from(
      new Set(
        (listaAtual || [])
          .flatMap((n) => n.bairros || n.bairrosDesejados || [])
          .filter((b) => b && String(b).trim() !== '')
          .map((b) => String(b).trim())
      )
    ).sort();

    return { tiposUnicos: tipos, bairrosUnicos: bairros };
  }, [listaAtual]);

  /**
   * ✅ base filtrada SEM tipo (pra contar por tipo nas abas)
   */
  const negociosBaseSemTipo = useMemo(() => {
    return aplicarFiltrosSemTipo(listaAtual || [], filtros || {}, aba);
  }, [listaAtual, filtros, aba]);

  /**
   * ✅ contagem para as abas rápidas (Todos + por tipo)
   */
  const tipoTabsCounts = useMemo(() => {
    const map = { all: negociosBaseSemTipo.length };
    (negociosBaseSemTipo || []).forEach((n) => {
      const t = String(n?.tipoImovel || '').trim();
      if (!t) return;
      map[t] = (map[t] || 0) + 1;
    });
    return map;
  }, [negociosBaseSemTipo]);

  /**
   * ✅ aplicar filtros COMPLETOS (inclui tipoImovel)
   */
  const negociosFiltrados = useMemo(() => {
    return (negociosBaseSemTipo || []).filter((n) => {
      if (filtros.tipoImovel && n.tipoImovel !== filtros.tipoImovel)
        return false;
      return true;
    });
  }, [negociosBaseSemTipo, filtros.tipoImovel]);

  // ✅ quando troca de aba (rede/meus), reseta filtros que não fazem sentido
  useEffect(() => {
    setFiltros((prev) => ({
      ...prev,
      corretorId: aba === 'meus' ? 'all' : prev.corretorId || 'all',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aba]);

  const handleNovo = () => {
    setEditing(null);
    setOpenModal(true);
  };

  const handleSave = async (dados) => {
    if (!usuario) return;
    setSalvando(true);

    try {
      if (editing) {
        await atualizarNegocio(editing.id, dados);
      } else {
        await criarNegocio({ usuario, dados });
      }
      setOpenModal(false);
      setEditing(null);
    } catch (err) {
      console.error(err);
      alert('Erro ao salvar negócio.');
    } finally {
      setSalvando(false);
    }
  };

  // ✅ WhatsApp do DONO do negócio (contato privado)
  const handleWhatsAppDono = async (negocioId) => {
    try {
      const dados = await getNegocioPrivado(negocioId);
      const phone = dados?.whatsapp || dados?.telefone;
      const link = phone ? toWhatsAppLink(phone) : null;

      if (!link) {
        alert('Dono do negócio sem WhatsApp/telefone cadastrado.');
        return;
      }

      window.open(link, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error(err);
      alert('Erro ao carregar contato do dono.');
    }
  };

  const handleVerContatos = async (id) => {
    try {
      const dados = await getNegocioPrivado(id);
      if (!dados) {
        alert('Nenhum contato cadastrado.');
        return;
      }

      const info = [];
      if (dados.whatsapp) info.push(`WhatsApp: ${dados.whatsapp}`);
      if (dados.telefone) info.push(`Telefone: ${dados.telefone}`);
      if (dados.email) info.push(`E-mail: ${dados.email}`);

      alert(info.join('\n') || 'Nenhum contato disponível.');
    } catch (err) {
      console.error(err);
      alert('Erro ao carregar contatos.');
    }
  };

  // =============================
  // ✅ MATCH LISTENERS
  // =============================
  const ensureMatchListener = (negocioId) => {
    if (!negocioId) return;
    if (unsubMatchByNegocio[negocioId]) return;

    const unsub = listenMatchesDoNegocio(negocioId, (lista) => {
      const ordenada = [...(lista || [])].sort((a, b) => {
        const ra = rankStatusVal(a?.status);
        const rb = rankStatusVal(b?.status);
        if (ra !== rb) return ra - rb;
        return Number(b?.score_match || 0) - Number(a?.score_match || 0);
      });

      setMatchesByNegocio((prev) => ({ ...prev, [negocioId]: ordenada }));
    });

    setUnsubMatchByNegocio((prev) => ({ ...prev, [negocioId]: unsub }));
  };

  const stopMatchListener = (negocioId) => {
    const fn = unsubMatchByNegocio[negocioId];
    if (typeof fn === 'function') fn();

    setUnsubMatchByNegocio((prev) => {
      const cp = { ...prev };
      delete cp[negocioId];
      return cp;
    });
  };

  const toggleMatch = async (negocio) => {
    const id = negocio?.id;
    if (!id) return;

    const nextOpen = matchAbertoId === id ? null : id;
    setMatchAbertoId(nextOpen);

    if (nextOpen === null) {
      stopMatchListener(id);
      return;
    }

    ensureMatchListener(id);

    if (matchesByNegocio[id]?.length) return;

    try {
      setLoadingMatchId(id);

      await gerarMatchesParaNegocio(id, {
        permitirTriangular: true,
        maxResultados: 12,
        constraints: {
          tolerancia_valor_percent: 10,
          valor_minimo_entrada_dinheiro: 0,
          permitir_busca_triangular: true,
        },
      });
    } catch (e) {
      console.error(e);
      setMatchesByNegocio((prev) => ({ ...prev, [id]: [] }));
    } finally {
      setLoadingMatchId(null);
    }
  };

  if (!usuario?.uid) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <div className="rounded-xl border bg-white p-6 text-center">
          <div className="text-lg font-semibold">Faça login para continuar</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 p-6">
      {/* 1) Header principal */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Central de Negócios
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Registre demandas e encontre oportunidades com seus sócios.
          </p>
        </div>

        <button
          onClick={handleNovo}
          className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 transition"
          title="Criar um novo negócio (seu)"
        >
          + Novo Negócio
        </button>
      </div>

      {/* 2) Tabs Rede/Meus */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setAba('rede')}
          className={`rounded-full px-5 py-2 text-sm font-medium transition-all inline-flex items-center ${
            aba === 'rede'
              ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
          }`}
        >
          Negócios da Rede
          <span
            className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              aba === 'rede'
                ? 'bg-white/20 text-white'
                : 'bg-gray-100 text-gray-700'
            }`}
            title="Total de negócios visíveis (rede)"
          >
            {totalRede}
          </span>
        </button>

        <button
          onClick={() => setAba('meus')}
          className={`rounded-full px-5 py-2 text-sm font-medium transition-all inline-flex items-center ${
            aba === 'meus'
              ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
          }`}
        >
          Meus Negócios
          <span
            className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              aba === 'meus'
                ? 'bg-white/20 text-white'
                : 'bg-gray-100 text-gray-700'
            }`}
            title="Total de negócios criados por você"
          >
            {totalMeus}
          </span>
        </button>
      </div>

      {/* 3) Filtros + tipo tabs + header colunas */}
      <div>
        <div className="mb-1">
          <NegociosFiltroBar
            filtros={filtros}
            onChange={setFiltros}
            listaTipos={tiposUnicos}
            listaBairros={bairrosUnicos}
            showCorretorSelect={aba === 'rede'}
            corretoresSelect={corretoresSelect}
          />
        </div>

        <TipoTabsBar
          filtros={filtros}
          onChange={setFiltros}
          counts={tipoTabsCounts}
        />

        <NegociosColumnsHeader />

        {/* LISTA */}
        {negociosFiltrados.length === 0 ? (
          <div className="mt-8 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-100 bg-gray-50 py-12 text-center">
            <p className="text-gray-500 text-sm font-medium">
              Nenhum negócio encontrado.
            </p>
            <p className="text-gray-400 text-xs mt-1">
              Tente ajustar seus filtros.
            </p>
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            {negociosFiltrados.map((n) => {
              const isOpen = matchAbertoId === n.id;
              const waCorretor = toWhatsAppLink(n.corretorWhatsapp || '');

              return (
                <React.Fragment key={n.id}>
                  <NegocioCard
                    negocio={n}
                    modo={aba === 'rede' ? 'comunidade' : 'meu'}
                    onOpenDetails={() => setNegocioAberto(n)}
                    onEditar={() => {
                      setEditing(n);
                      setOpenModal(true);
                    }}
                    onVerContatos={() => handleVerContatos(n.id)}
                    onMatchClick={() => toggleMatch(n)}
                    matchAberto={isOpen}
                    showWhatsAppDono={aba === 'rede'}
                    onWhatsAppDono={handleWhatsAppDono}
                    whatsappCorretorLink={waCorretor || null}
                    showWhatsAppCorretor={true}
                  />

                  {/* ✅ Matches */}
                  {isOpen && (
                    <div className="ml-4 mr-4 -mt-2 mb-4 rounded-b-2xl border-x border-b border-blue-100 bg-blue-50/30 p-4">
                      {loadingMatchId === n.id ? (
                        <div className="text-xs text-blue-700 font-medium">
                          Buscando oportunidades...
                        </div>
                      ) : (matchesByNegocio[n.id] || []).length === 0 ? (
                        <div className="text-xs text-gray-500">
                          Ainda não foi possível gerar matches para este
                          negócio.
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2">
                          {(matchesByNegocio[n.id] || [])
                            .slice(0, 6)
                            .map((m) => (
                              <MatchCard
                                key={
                                  m.id ||
                                  m.match_id ||
                                  `${m.tipo_match}-${m.imovel_doc_id}-${m.item_b_id}-${m.item_c_id || ''}`
                                }
                                match={m}
                                onOpen={(matchObj) => setMatchAberto(matchObj)}
                                onChat={() => {}}
                              />
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

      {/* ✅ MODAL EXPANDIDO */}
      <NegocioDetailsModal
        open={!!negocioAberto}
        negocio={negocioAberto}
        modo={aba === 'rede' ? 'comunidade' : 'meu'}
        onClose={() => setNegocioAberto(null)}
        onEditar={(neg) => {
          setNegocioAberto(null);
          setEditing(neg);
          setOpenModal(true);
        }}
      />

      <NegocioFormModal
        open={openModal}
        onClose={() => !salvando && (setOpenModal(false), setEditing(null))}
        onSave={handleSave}
        initialData={editing}
      />

      <MatchCardModal
        open={!!matchAberto}
        match={matchAberto}
        onClose={() => setMatchAberto(null)}
        onOpenImovel={(idImovel) => navigate(`/imovel/${idImovel}`)}
      />
    </div>
  );
}
